import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

interface TimeoffFormData {
  timeOffType: string;
  clientDetails: string;
  fromDate: string;
  toDate: string;
  timeOffPricing: string;
  currency: string;
  pricing: string;
  price: string;
  hoursForDay: string;
}

interface AddTimeoffSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: TimeoffFormData) => void;
}

const AddTimeoffSidebar: React.FC<AddTimeoffSidebarProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<TimeoffFormData>({
    timeOffType: '',
    clientDetails: '',
    fromDate: '',
    toDate: '',
    timeOffPricing: 'Paid',
    currency: '',
    pricing: 'Hourly',
    price: '',
    hoursForDay: '08',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    handleCancel();
  };

  const handleCancel = () => {
    setFormData({
      timeOffType: '',
      clientDetails: '',
      fromDate: '',
      toDate: '',
      timeOffPricing: 'Paid',
      currency: '',
      pricing: 'Hourly',
      price: '',
      hoursForDay: '08',
    });
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      <div className="sidebar-overlay" onClick={onClose}></div>
      <div className="add-timeoff-sidebar">
        <div className="sidebar-header">
          <h2 className="sidebar-title">
            {t('Add Timeoff Record') || 'Add Timeoff Record'}
          </h2>
          <button
            type="button"
            className="sidebar-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="#000000"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="sidebar-body">
          {/* Time off type */}
          <div className="form-row">
            <div className="form-field">
              <label className="form-label">
                {t('Time off type') || 'Time off type'}
              </label>
              <div className="form-select-wrapper">
                <select
                  className="form-select"
                  value={formData.timeOffType}
                  onChange={(e) =>
                    setFormData({ ...formData, timeOffType: e.target.value })
                  }
                >
                  <option value="">
                    {t('Select time off type') || 'Select time off type'}
                  </option>
                  <option value="sick">{t('Sick Leave') || 'Sick Leave'}</option>
                  <option value="vacation">{t('Vacation') || 'Vacation'}</option>
                  <option value="personal">{t('Personal') || 'Personal'}</option>
                </select>
                <svg
                  className="select-arrow"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="#000000"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Client details */}
          <div className="form-row">
            <div className="form-field">
              <label className="form-label">
                {t('Client details') || 'Client details'}
              </label>
              <div className="form-select-wrapper">
                <select
                  className="form-select"
                  value={formData.clientDetails}
                  onChange={(e) =>
                    setFormData({ ...formData, clientDetails: e.target.value })
                  }
                >
                  <option value="">
                    {t('Select client') || 'Select client'}
                  </option>
                  <option value="client1">Client 1</option>
                  <option value="client2">Client 2</option>
                </select>
                <svg
                  className="select-arrow"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="#000000"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* From Date & To Date */}
          <div className="form-row form-row-split">
            <div className="form-field">
              <label className="form-label">
                {t('From Date') || 'From Date'}
              </label>
              <div className="form-input-wrapper">
                <input
                  type="date"
                  className="form-input"
                  value={formData.fromDate}
                  onChange={(e) =>
                    setFormData({ ...formData, fromDate: e.target.value })
                  }
                  placeholder="dd/mm/yyyy"
                />
                <svg
                  className="input-icon"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M6.66667 1.66669V4.16669M13.3333 1.66669V4.16669M2.91667 7.57502H17.0833M17.9167 7.08335V14.1667C17.9167 16.6667 16.6667 18.3334 13.75 18.3334H6.25C3.33333 18.3334 2.08333 16.6667 2.08333 14.1667V7.08335C2.08333 4.58335 3.33333 2.91669 6.25 2.91669H13.75C16.6667 2.91669 17.9167 4.58335 17.9167 7.08335Z"
                    stroke="#68727D"
                    strokeWidth="1.5"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M13.0791 11.4167H13.0866M13.0791 13.9167H13.0866M9.99607 11.4167H10.0036M9.99607 13.9167H10.0036M6.91209 11.4167H6.91957M6.91209 13.9167H6.91957"
                    stroke="#68727D"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
            <div className="form-field">
              <label className="form-label">
                {t('To Date') || 'To Date'}
              </label>
              <div className="form-input-wrapper">
                <input
                  type="date"
                  className="form-input"
                  value={formData.toDate}
                  onChange={(e) =>
                    setFormData({ ...formData, toDate: e.target.value })
                  }
                  placeholder="dd/mm/yyyy"
                />
                <svg
                  className="input-icon"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M6.66667 1.66669V4.16669M13.3333 1.66669V4.16669M2.91667 7.57502H17.0833M17.9167 7.08335V14.1667C17.9167 16.6667 16.6667 18.3334 13.75 18.3334H6.25C3.33333 18.3334 2.08333 16.6667 2.08333 14.1667V7.08335C2.08333 4.58335 3.33333 2.91669 6.25 2.91669H13.75C16.6667 2.91669 17.9167 4.58335 17.9167 7.08335Z"
                    stroke="#68727D"
                    strokeWidth="1.5"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M13.0791 11.4167H13.0866M13.0791 13.9167H13.0866M9.99607 11.4167H10.0036M9.99607 13.9167H10.0036M6.91209 11.4167H6.91957M6.91209 13.9167H6.91957"
                    stroke="#68727D"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Time Off Pricing & Currency */}
          <div className="form-row form-row-split">
            <div className="form-field">
              <label className="form-label">
                {t('Time Off Pricing') || 'Time Off Pricing'}
              </label>
              <div className="form-select-wrapper">
                <select
                  className="form-select"
                  value={formData.timeOffPricing}
                  onChange={(e) =>
                    setFormData({ ...formData, timeOffPricing: e.target.value })
                  }
                >
                  <option value="Paid">{t('Paid') || 'Paid'}</option>
                  <option value="Unpaid">{t('Unpaid') || 'Unpaid'}</option>
                </select>
                <svg
                  className="select-arrow"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="#000000"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
            <div className="form-field">
              <label className="form-label">
                {t('Currency') || 'Currency'}
              </label>
              <div className="form-select-wrapper">
                <select
                  className="form-select"
                  value={formData.currency}
                  onChange={(e) =>
                    setFormData({ ...formData, currency: e.target.value })
                  }
                >
                  <option value="">
                    {t('Select currency') || 'Select currency'}
                  </option>
                  <option value="SEK">SEK</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
                <svg
                  className="select-arrow"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="#68727D"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Pricing & Price */}
          <div className="form-row form-row-split">
            <div className="form-field">
              <label className="form-label">
                {t('Pricing') || 'Pricing'}
              </label>
              <div className="form-select-wrapper">
                <select
                  className="form-select"
                  value={formData.pricing}
                  onChange={(e) =>
                    setFormData({ ...formData, pricing: e.target.value })
                  }
                >
                  <option value="Hourly">{t('Hourly') || 'Hourly'}</option>
                  <option value="Daily">{t('Daily') || 'Daily'}</option>
                  <option value="Fixed">{t('Fixed') || 'Fixed'}</option>
                </select>
                <svg
                  className="select-arrow"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="#000000"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
            <div className="form-field">
              <label className="form-label">
                {t('Price') || 'Price'}
              </label>
              <div className="form-input-wrapper">
                <input
                  type="number"
                  className="form-input"
                  value={formData.price}
                  onChange={(e) =>
                    setFormData({ ...formData, price: e.target.value })
                  }
                  placeholder={t('Enter value') || 'Enter value'}
                />
                <span className="input-currency">Kr</span>
              </div>
            </div>
          </div>

          {/* Hours for day */}
          <div className="form-row">
            <div className="form-field">
              <label className="form-label">
                {t('Hours for day') || 'Hours for day'}
              </label>
              <div className="form-select-wrapper">
                <select
                  className="form-select"
                  value={formData.hoursForDay}
                  onChange={(e) =>
                    setFormData({ ...formData, hoursForDay: e.target.value })
                  }
                >
                  <option value="01">01</option>
                  <option value="02">02</option>
                  <option value="03">03</option>
                  <option value="04">04</option>
                  <option value="05">05</option>
                  <option value="06">06</option>
                  <option value="07">07</option>
                  <option value="08">08</option>
                  <option value="09">09</option>
                  <option value="10">10</option>
                  <option value="11">11</option>
                  <option value="12">12</option>
                </select>
                <svg
                  className="select-arrow"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="#000000"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div className="sidebar-footer">
            <button
              type="button"
              className="btn-cancel"
              onClick={handleCancel}
            >
              {t('Cancel') || 'Cancel'}
            </button>
            <button type="submit" className="btn-submit">
              {t('Submit') || 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </>,
    document.body
  );
};

export default AddTimeoffSidebar;
